import api from '../../config/axios.js';
import moment from 'moment';
import 'moment/dist/locale/pt-br';

moment.locale('pt-br');

function normalizeChat(chat) {
    if (!chat) {
        return chat;
    }

    const lastMessageDate =
        chat.last_message?.updated_at ||
        chat.last_message?.created_at ||
        null;

    return {
        ...chat,
        last_message_formatted: lastMessageDate
            ? moment(lastMessageDate).fromNow()
            : null
    };
}

function sortChatsByLastMessage(chats) {
    return [...chats].sort((chatA, chatB) => {
        const chatADate =
            chatA.last_message?.updated_at ||
            chatA.last_message?.created_at ||
            chatA.updated_at ||
            chatA.created_at ||
            null;
        const chatBDate =
            chatB.last_message?.updated_at ||
            chatB.last_message?.created_at ||
            chatB.updated_at ||
            chatB.created_at ||
            null;

        const chatATime = chatADate ? new Date(chatADate).getTime() : 0;
        const chatBTime = chatBDate ? new Date(chatBDate).getTime() : 0;

        return chatBTime - chatATime;
    });
}

function mergeChats(currentChats, incomingChats) {
    const currentMap = new Map(
        currentChats.map(chat => [chat.id, normalizeChat(chat)])
    );

    incomingChats.forEach(chat => {
        const normalizedChat = normalizeChat(chat);
        const currentChat = currentMap.get(normalizedChat.id);

        currentMap.set(normalizedChat.id, {
            ...currentChat,
            ...normalizedChat,
            client: normalizedChat.client || currentChat?.client || null,
            last_message: normalizedChat.last_message || currentChat?.last_message || null
        });
    });

    return sortChatsByLastMessage(Array.from(currentMap.values()));
}

function normalizeMessage(message) {
    if (!message) {
        return message;
    }

    return {
        ...message,
        date_formatted: message.created_at
            ? moment(message.created_at).format('HH:mm:ss DD/MM/YY')
            : null
    };
}

function isAttendantChat(chat) {
    return chat?.speak_to_attendant === true || chat?.speak_to_attendant === 1 || chat?.speak_to_attendant === '1';
}

function getMessageUniqueKey(message) {
    if (!message) {
        return null;
    }
    if (message.id) return 'message:' + message.id;

    return [
        message.chat_id,
        message.sent_by,
        message.created_at,
        message.message
    ].join('|') || message.id || null;
}

function mergeMessages(currentMessages, incomingMessages) {
    const messageMap = new Map();

    [...currentMessages, ...incomingMessages].forEach(message => {
        const normalizedMessage = normalizeMessage(message);
        const uniqueKey = getMessageUniqueKey(normalizedMessage);

        messageMap.set(uniqueKey, { ...messageMap.get(uniqueKey), ...normalizedMessage });
    });

    return Array.from(messageMap.values()).sort((messageA, messageB) => {
        const messageATime = messageA.created_at ? new Date(messageA.created_at).getTime() : 0;
        const messageBTime = messageB.created_at ? new Date(messageB.created_at).getTime() : 0;

        return messageBTime - messageATime;
    });
}

const state = {
    chatList: [],
    currentChat: null,
    nextPageUrl: '/api/chats',
    loadingChatList: false,
    loadingMessagesChat: false,
    nextPageUrlMessagesChat: '/api/chat/chatId/messages',
    messagesChat: [],
    pendingMessagesChat: [],
    loadingChatById: []
};

const getters = {
    getChatList: state => state.chatList,
    getCurrentChat: state => state.currentChat,
    getNextPageUrl: state => state.nextPageUrl,
    isLoadingChatList: state => state.loadingChatList,
    isLoadingMessagesChat: state => state.loadingMessagesChat,
    getNextPageUrlMessagesChat: state => state.nextPageUrlMessagesChat,
    getMessagesChat: state => state.messagesChat,
    getPendingMessagesChat: state => state.pendingMessagesChat,
    getLoadingChatById: state => state.loadingChatById
};

const actions = {
    async fetchFinishAttendant({ commit }, params) {
        await api.axios.post('/api/attendant/finish', params).catch(() => {
            return;
        }).then(response => {
            if (response && response.data) {
                window.location.reload();
            }
        });
    },
    async resetChatList({ commit }) {
        commit('setChatList', []);
    },
    async receiveMessageWebsocket({ commit, state }, message) {
        if (message.chat) commit('updateChatMetadata', {
            ...message.chat,
            client: message.chat.client || message.client
        });
        // Automation also emits metadata, but only an actual handoff belongs
        // in the human inbox. Do not fetch or mark these chats as pending.
        if (message.chat && Object.prototype.hasOwnProperty.call(message.chat, 'speak_to_attendant') && !isAttendantChat(message.chat)) return;

        const chatIndex = state.chatList.findIndex(
            chat => chat.id === message.chat_id
        );

        if (chatIndex !== -1) {
            commit('receiveMessageWebsocket', message);
            if (state.currentChat == null || state.currentChat.id != message.chat_id) {
                commit('setPendingMessagesChat', message.chat_id);
            }
            return;
        }

        if (state.loadingChatById.includes(message.chat_id)) {
            if (state.currentChat == null || state.currentChat.id != message.chat_id) {
                commit('setPendingMessagesChat', message.chat_id);
            }
            return;
        }

        commit('addLoadingChatById', message.chat_id);

        try {
            const response = await api.axios.get(`/api/chat/${message.chat_id}`).catch(() => null);
            if (response && response.data) {
                commit('setChatList', response.data.data);
                commit('setPendingMessagesChat', message.chat_id);
            }
        } finally {
            commit('removeLoadingChatById', message.chat_id);
        }
    },
    async fetchChatList({ commit, getters }, pageUrl) {
        if (pageUrl == null || getters.isLoadingChatList) {
            return;
        }

        commit('setLoadingChatList', true);

        await api.axios.get(pageUrl).catch(() => {
            return;
        }).then(response => {
            if (response && response.data) {
                commit('nextPageUrl', response.data.next_page_url);
                commit('setChatList', response.data.data);
                commit('setLoadingChatList', false);
            }
        });
    },
    async syncChatList({ commit, getters }) {
        if (getters.isLoadingChatList) {
            return;
        }

        commit('setLoadingChatList', true);

        await api.axios.get('/api/chats').catch(() => {
            return;
        }).then(response => {
            if (response && response.data) {
                commit('syncChatList', response.data.data);
                commit('setLoadingChatList', false);
            }
        });
    },
    async fetchMessagesChat({ commit, getters }, pageUrlMessageChat) {
        if (pageUrlMessageChat == null || getters.isLoadingMessagesChat) {
            return;
        }

        commit('setLoadingMessagesChat', true);

        await api.axios.get(pageUrlMessageChat).catch(() => {
            return;
        }).then(response => {
            if (response && response.data) {
                commit('setNextPageUrlMessagesChat', response.data.next_page_url);
                commit('setMessagesChat', response.data.data);
                commit('setLoadingMessagesChat', false);
            }
        });
    },
    async sendMessageChat({ commit }, params) {
        const response = await api.axios.post('/api/create/message/chat', params);
        if (response?.data) commit('appendNewMessageChat', response.data);
        return response?.data;
    },
    async sendTemplateMessageChat({ commit }, params) {
        const response = await api.axios.post('/api/create/template/message/chat', params);
        if (response?.data) commit('appendNewMessageChat', response.data);
        return response?.data;
    },
    async setCurrentChat({ commit, getters }, chat) {
        if (chat != null && getters.getCurrentChat != null && getters.getCurrentChat.id == chat.id) {
            return;
        }

        commit('resetMessagesChat', []);
        commit('setCurrentChat', chat);
        if (chat != null) {
            commit('removePendingMessagesChat', chat.id);
        }
    }
};

const mutations = {
    updateChatMetadata(state, chat) {
        const hasHandoff = Object.prototype.hasOwnProperty.call(chat, 'speak_to_attendant');
        if (hasHandoff && !isAttendantChat(chat)) {
            state.chatList = state.chatList.filter(item => item.id !== chat.id);
            state.pendingMessagesChat = state.pendingMessagesChat.filter(id => id !== chat.id);
            if (state.currentChat?.id === chat.id) {
                state.currentChat = null;
                state.messagesChat = [];
            }
            return;
        }
        // Partial category updates may update an existing chat, but cannot
        // introduce an unknown chat without evidence of human handoff.
        if (isAttendantChat(chat) || state.chatList.some(item => item.id === chat.id)) {
            state.chatList = mergeChats(state.chatList, [chat]);
        }
        if (state.currentChat?.id === chat.id) state.currentChat = { ...state.currentChat, ...chat };
    },
    setChatList(state, data) {
        const chats = Array.isArray(data) ? data : (data ? [data] : []);
        state.chatList = mergeChats(state.chatList, chats);
    },
    syncChatList(state, data) {
        const chats = Array.isArray(data) ? data : (data ? [data] : []);
        state.chatList = sortChatsByLastMessage(chats.map(normalizeChat));
    },
    receiveMessageWebsocket(state, data) {
        state.chatList = mergeChats(
            state.chatList,
            state.chatList
                .filter(chat => chat.id == data.chat_id)
                .map(chat => ({
                    ...chat,
                    last_message: data
                }))
        );

        if (state.currentChat && state.currentChat.id === data.chat_id) {
            state.messagesChat = mergeMessages(state.messagesChat, [data]);
            if (data.provider === 'cloud_api' && data.direction === 'inbound') {
                state.currentChat = {
                    ...state.currentChat,
                    service_window_open: true,
                    service_window_expires_at: data.created_at
                        ? moment(data.created_at).add(24, 'hours').toISOString()
                        : state.currentChat.service_window_expires_at
                };
            }
        }
    },
    receiveChatAndMessageWebsocket(state, data) {
        state.chatList = mergeChats(state.chatList, [data]);
    },
    nextPageUrl(state, data) {
        state.nextPageUrl = data;
    },
    setLoadingChatList(state, data) {
        state.loadingChatList = data;
    },
    setCurrentChat(state, data) {
        state.currentChat = data;
    },
    resetMessagesChat(state, data) {
        state.messagesChat = data;
    },
    setMessagesChat(state, data) {
        const messages = Array.isArray(data) ? data : (data ? [data] : []);
        state.messagesChat = mergeMessages(state.messagesChat, messages);
    },
    setNextPageUrlMessagesChat(state, data) {
        state.nextPageUrlMessagesChat = data;
    },
    setLoadingMessagesChat(state, data) {
        state.loadingMessagesChat = data;
    },
    addLoadingChatById(state, id) {
        if (!state.loadingChatById.includes(id)) {
            state.loadingChatById = [...state.loadingChatById, id];
        }
    },
    removeLoadingChatById(state, id) {
        state.loadingChatById = state.loadingChatById.filter(item => item !== id);
    },
    appendNewMessageChat(state, data) {
        state.messagesChat = mergeMessages(state.messagesChat, [data]);
    },
    setPendingMessagesChat(state, data) {
        const exists = state.pendingMessagesChat.some(item => item === data);

        if (!exists) {
            state.pendingMessagesChat = [...state.pendingMessagesChat, data];
        }
    },
    removePendingMessagesChat(state, id) {
        state.pendingMessagesChat = state.pendingMessagesChat.filter(
            item => item !== id
        );
    }
};

export default {
    state,
    getters,
    actions,
    mutations
};
